import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { CartApi } from "services/CartApi";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";

interface CartContextType {
  userCart: any[];
  setUserCart: (cart: any) => void;
  addToCart: (body: any) => void;
  removeItem: (body: any) => void;
  updateQuantity: (body: any) => void;
  productSelected: any[];
  setProductSelected: (selected: any[]) => void;
  setIsRefreshCart: (is: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [userCart, setUserCart] = useState<any[]>([]);
  const [productSelected, setProductSelected] = useState<any>([]);
  const [isRefreshCart, setIsRefreshCart] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (userCart.length > 0 && !isMounted) {
      const newProductSelected: any = [];
      userCart.forEach((item: any) => {
        const productSelectedItem = {
          productId: item.product_id,
          isSelected: true,
        };
        newProductSelected.push(productSelectedItem);
      });

      setProductSelected((prevSelected: any) => {
        const mergedArray = [...prevSelected, ...newProductSelected];
        const uniqueProducts = Array.from(
          new Map(mergedArray.map((p) => [p.productId, p])).values()
        );
        return uniqueProducts;
      });
      setIsMounted(true);
    }
  }, [userCart, isMounted]);

  useEffect(() => {
    const getUserCart = async () => {
      try {
        const response = await CartApi.getItems();
        if (response.status == 200) {
          setUserCart(response.data.data);
          setIsMounted(false);
        }
      } catch (error) {
        Helpers.notification.error("Can't get user cart");
        errorLog("CartContext.tsx - getUserCart", error);
      }
      setIsRefreshCart(false);
    };
    if (isAuthenticated || isRefreshCart) {
      getUserCart();
    }
  }, [isAuthenticated, isRefreshCart]);

  const addToCart = async (body: any) => {
    try {
      const response = await CartApi.addItem(body);
      if (
        response.status == 200 &&
        response.data.message == "Product added to cart"
      ) {
        setIsRefreshCart(true);
      }
    } catch (error: any) {
      if (
        error.status === 400 &&
        error.response.data.message ==
          "Product quantity is less than requested quantity"
      ) {
        Helpers.notification.warning(
          "You have reached the maximum quantity for this product"
        );
        return;
      }
      Helpers.notification.error("Can't add to cart");
      errorLog("CartContext.tsx - addToCart", error);
    }
  };

  const removeItem = async (body: any) => {
    try {
      const response = await CartApi.removeItem(body);
      if (response.status == 200) {
        const filteredCart = userCart.filter(
          (item) => item.product_id != body.product_id
        );
        const filteredSelected = productSelected.filter(
          (item: any) => item.productId != body.product_id
        );
        setUserCart(filteredCart);
        setProductSelected(filteredSelected);
      }
    } catch (error) {
      Helpers.notification.error("Can't remove this product");
      errorLog("CartContext.tsx - removeItem", error);
    }
  };

  const updateQuantity = async (body: {
    product_id: number;
    quantity: number;
  }) => {
    try {
      await CartApi.updateItem(body);
    } catch (error) {
      Helpers.notification.error("Can't update this product");
      errorLog("CartContext.tsx - updateQuantity", error);
    }
  };

  return (
    <CartContext.Provider
      value={{
        userCart,
        setUserCart,
        addToCart,
        removeItem,
        updateQuantity,
        productSelected,
        setProductSelected,
        setIsRefreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
